import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { match } from "ts-pattern";

import { applyThemePreset } from "embedding-sdk-shared/lib/apply-theme-preset";
import { useListEmbeddingThemesQuery } from "metabase/api/embedding-theme";
import { METABASE_CONFIG_IS_PROXY_FIELD_NAME } from "metabase/embedding/embedding-iframe-sdk/constants";
// we import the equivalent of embed.js so that we don't add extra loading time
// by appending the script
import { setupConfigWatcher } from "metabase/embedding/embedding-iframe-sdk/embed";
import type { SdkIframeEmbedBaseSettings } from "metabase/embedding/embedding-iframe-sdk/types/embed";
import { buildEmbedAttributes } from "metabase/embedding/embedding-iframe-sdk-setup/utils/build-embed-attributes";
import type { MetabaseTheme } from "metabase/embedding-sdk/theme";
import { useSetting } from "metabase/settings";
import { Card } from "metabase/ui";
import { colors as defaultMetabaseColors } from "metabase/ui/colors";

import { useSdkIframeEmbedSetupContext } from "../context";
import { getDerivedDefaultColorsForEmbedFlow } from "../utils/derived-colors-for-embed-flow";
import { resolveSavedTheme } from "../utils/resolve-saved-theme";
import { getConfigurableThemeColors } from "../utils/theme-colors";

import { EmbedPreviewLoadingOverlay } from "./EmbedPreviewLoadingOverlay";
import S from "./SdkIframeEmbedPreview.module.css";

declare global {
  interface Window {
    metabaseConfig?: Partial<SdkIframeEmbedBaseSettings> & {
      [METABASE_CONFIG_IS_PROXY_FIELD_NAME]?: boolean;
    };
  }
}

const SdkIframeEmbedPreviewInner = () => {
  const {
    isSimpleEmbedFeatureAvailable,
    experience,
    settings,
    guestEmbedSignedTokenForPreview,
  } = useSdkIframeEmbedSetupContext();
  const [isLoading, setIsLoading] = useState(true);

  const instanceUrl = useSetting("site-url");
  const applicationColors = useSetting("application-colors");

  const { data: savedThemes } = useListEmbeddingThemesQuery(undefined, {
    skip: !isSimpleEmbedFeatureAvailable || !settings.theme?.id,
  });

  const embedElementRef = useRef<HTMLElement | null>(null);

  const handleEmbedReady = useCallback(() => setIsLoading(false), []);

  // Attach the "ready" listener through a ref callback so it re-arms every time
  // the embed element is recreated. The container remounts whenever chart colors
  // change (see the `key` on the Card below), and a listener registered in an
  // effect keyed on `componentName` would stay bound to the discarded element,
  // leaving the loading overlay stuck forever.
  const setEmbedElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (embedElementRef.current) {
        embedElementRef.current.removeEventListener("ready", handleEmbedReady);
      }

      embedElementRef.current = node;

      if (node) {
        setIsLoading(true);
        node.addEventListener("ready", handleEmbedReady);
      }
    },
    [handleEmbedReady],
  );

  const defineMetabaseConfig = useCallback(
    (metabaseConfig: SdkIframeEmbedBaseSettings) => {
      window.metabaseConfig = metabaseConfig;

      if (!window.metabaseConfig[METABASE_CONFIG_IS_PROXY_FIELD_NAME]) {
        setupConfigWatcher();
      }
    },
    [],
  );
  const cleanupMetabaseConfig = useCallback(() => {
    if (window.metabaseConfig) {
      delete window.metabaseConfig;
    }
  }, []);

  const effectiveTheme = useMemo(
    () => resolveSavedTheme({ theme: settings.theme, savedThemes }),
    [settings.theme, savedThemes],
  );

  const theme = useMemo(() => {
    if (effectiveTheme?.preset) {
      return applyThemePreset(effectiveTheme);
    }

    // TODO(EMB-696): There is a bug in the SDK where if we set the theme back to undefined,
    // some color will not be reset to the default (e.g. text color, CSS variables).
    // We can remove this block once EMB-696 is fixed.
    const defaultTheme: MetabaseTheme = {
      colors: Object.fromEntries(
        getConfigurableThemeColors().map((color) => [
          color.key,
          applicationColors?.[color.originalColorKey] ??
            defaultMetabaseColors[color.originalColorKey],
        ]),
      ),
    };

    const derivedTheme = getDerivedDefaultColorsForEmbedFlow({
      isSimpleEmbedFeatureAvailable,
      theme: effectiveTheme ?? defaultTheme,
      applicationColors: applicationColors ?? undefined,
    });

    return derivedTheme;
  }, [isSimpleEmbedFeatureAvailable, applicationColors, effectiveTheme]);

  const metabaseConfig = useMemo(
    () => ({
      instanceUrl,
      theme,
      useExistingUserSession: true,
      isGuest: settings.isGuest,
      allowedCustomVisualizations: settings.isGuest
        ? undefined
        : settings.allowedCustomVisualizations,
    }),
    [
      instanceUrl,
      theme,
      settings.isGuest,
      settings.allowedCustomVisualizations,
    ],
  );

  // initial configuration, needed so that the element finds the config on first render
  if (!window.metabaseConfig?.instanceUrl) {
    defineMetabaseConfig(metabaseConfig);
  }

  useEffect(() => {
    defineMetabaseConfig(metabaseConfig);
  }, [metabaseConfig, defineMetabaseConfig]);

  useEffect(
    () => () => {
      cleanupMetabaseConfig();
    },
    [cleanupMetabaseConfig],
  );

  const attributes = buildEmbedAttributes({
    experience,
    settings,
    token: guestEmbedSignedTokenForPreview,
    wrapWithQuotes: false,
  });

  // The embed's own loading indicator only appears after the iframe loads, so we
  // show a "fake" loader (cleared by the "ready" event) to avoid a white flash.
  // The ref callback wires up that listener — see `setEmbedElementRef` above.
  const embedAttributes = { ...attributes, ref: setEmbedElementRef };

  return (
    <Card
      className={S.EmbedPreviewIframe}
      id="iframe-embed-container"
      style={{ backgroundColor: theme?.colors?.background }}
      h="100%"
      pos="relative"
      // `key` forces a remount when chart colors change. The embed runtime
      // propagates other theme keys via CSS variables, but chart colors are
      // applied through a module-level side effect on mount, so already-rendered
      // charts don't pick up updates without a full remount.
      key={JSON.stringify(theme?.colors?.charts)}
    >
      {match(settings)
        .with(
          { componentName: "metabase-question", template: "exploration" },
          () => createElement("metabase-question", embedAttributes),
        )
        .with({ componentName: "metabase-question" }, () =>
          createElement("metabase-question", embedAttributes),
        )
        .with({ componentName: "metabase-dashboard" }, () =>
          createElement("metabase-dashboard", embedAttributes),
        )
        .with({ componentName: "metabase-browser" }, () =>
          createElement("metabase-browser", embedAttributes),
        )
        .with({ componentName: "metabase-metabot" }, () =>
          createElement("metabase-metabot", embedAttributes),
        )
        .exhaustive()}

      <EmbedPreviewLoadingOverlay
        isVisible={isLoading}
        bg={theme?.colors?.background}
      />
    </Card>
  );
};

export const SdkIframeEmbedPreview = () => {
  const { settings } = useSdkIframeEmbedSetupContext();

  const remountKey = useMemo(
    () =>
      JSON.stringify({
        // We must re-mount preview when `isGuest` setting is changed
        // to force its change for embed.js
        isGuest: settings.isGuest,
      }),
    [settings.isGuest],
  );

  return <SdkIframeEmbedPreviewInner key={remountKey} />;
};
